import { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Flex, Grid, VStack } from '@chakra-ui/react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { LuArrowLeft, LuCircleCheck, LuPlus, LuX } from 'react-icons/lu'
import { Modal, PageHeader, Text, toaster } from '@minetrace/ui'
import { brand, themeColors } from '../../theme'
import { settlementsService } from '../../api/services/settlements.service'
import type {
  BuildSettlementRequest,
  SettlementLineRequest,
  SettlementMineralPriceRequest,
  SettlementPreviewResponse,
} from '../../api/models/response'

interface ExtraLineForm {
  kind: 'OtherCredit' | 'OtherDebit'
  description: string
  amount: string
}

const formatUsd = (v: number | null | undefined) =>
  v == null
    ? '-'
    : `$ ${v.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const formatNumber = (v: number | null | undefined, digits = 3) =>
  v == null ? '-' : v.toLocaleString('es-PE', { minimumFractionDigits: digits, maximumFractionDigits: digits })

export default function LiquidarLotePage() {
  const colors = themeColors
  const navigate = useNavigate()
  const { shipmentId } = useParams<{ shipmentId: string }>()
  const [searchParams] = useSearchParams()
  const readOnly = searchParams.get('readOnly') === '1'

  const [preview, setPreview] = useState<SettlementPreviewResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  const [prices, setPrices] = useState<Record<string, string>>({})
  const [extras, setExtras] = useState<ExtraLineForm[]>([])
  const [extraModalOpen, setExtraModalOpen] = useState(false)
  const [draftExtra, setDraftExtra] = useState<ExtraLineForm>({
    kind: 'OtherCredit',
    description: '',
    amount: '',
  })

  const buildRequest = useCallback((): BuildSettlementRequest => {
    const mineralPrices: SettlementMineralPriceRequest[] = Object.entries(prices)
      .filter(([, v]) => v.trim() !== '')
      .map(([mineralTypeId, v]) => ({
        mineralTypeId,
        unitPriceUsd: parseFloat(v.replace(',', '.')) || 0,
      }))

    const extraLines: SettlementLineRequest[] = extras
      .filter((e) => e.description.trim() && e.amount.trim())
      .map((e) => ({
        kind: e.kind,
        description: e.description.trim(),
        amount: parseFloat(e.amount.replace(',', '.')) || 0,
      }))

    return { mineralPrices, extraLines }
  }, [prices, extras])

  // Carga inicial: pide preview con precios vacios
  useEffect(() => {
    if (!shipmentId) return
    setLoading(true)
    settlementsService
      .preview(shipmentId, { mineralPrices: [], extraLines: [] })
      .then(setPreview)
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Error cargando preview'
        toaster.error({ description: msg })
      })
      .finally(() => setLoading(false))
  }, [shipmentId])

  // Recalcular preview cuando cambian precios o extras (debounce simple)
  useEffect(() => {
    if (!shipmentId || loading) return
    const t = setTimeout(() => {
      settlementsService
        .preview(shipmentId, buildRequest())
        .then(setPreview)
        .catch(() => {
          /* mantener preview actual */
        })
    }, 350)
    return () => clearTimeout(t)
  }, [shipmentId, buildRequest, loading])

  const handlePriceChange = (mineralTypeId: string, value: string) => {
    setPrices((prev) => ({ ...prev, [mineralTypeId]: value }))
  }

  const addExtra = () => {
    if (!draftExtra.description.trim() || !draftExtra.amount.trim()) {
      toaster.error({ description: 'Complete descripcion e importe.' })
      return
    }
    setExtras((prev) => [...prev, draftExtra])
    setDraftExtra({ kind: 'OtherCredit', description: '', amount: '' })
    setExtraModalOpen(false)
  }

  const removeExtra = (idx: number) => {
    setExtras((prev) => prev.filter((_, i) => i !== idx))
  }

  const handleLiquidar = async () => {
    if (!shipmentId || !preview) return
    setSubmitting(true)
    try {
      await settlementsService.build(shipmentId, buildRequest())
      toaster.success({ description: 'Liquidacion creada exitosamente.' })
      navigate('/app/cierre/liquidaciones')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error creando liquidacion'
      toaster.error({ description: msg })
    } finally {
      setSubmitting(false)
    }
  }

  const mineralLines = useMemo(
    () => preview?.lines.filter((l) => l.kind === 'MineralPayment') ?? [],
    [preview],
  )
  const otherLines = useMemo(
    () =>
      preview?.lines.filter((l) => l.kind === 'OtherCredit' || l.kind === 'OtherDebit') ?? [],
    [preview],
  )
  const maquilaLine = useMemo(() => preview?.lines.find((l) => l.kind === 'Maquila'), [preview])

  return (
    <VStack gap={5} alignItems="stretch">
      <Flex justifyContent="space-between" alignItems="center">
        <Flex gap={3} alignItems="center">
          <Box
            as="button"
            cursor="pointer"
            color={colors.textMuted}
            _hover={{ color: brand.primary }}
            onClick={() => navigate('/app/cierre/liquidaciones')}
          >
            <LuArrowLeft size={20} />
          </Box>
          <PageHeader
            title={readOnly ? 'Detalle de Liquidación' : 'Liquidar Lote'}
            subtitle={preview ? `${preview.supplierName} — ${preview.batchCode}` : 'Cargando...'}
            colors={colors}
          />
        </Flex>
      </Flex>

      {loading && <Text>Cargando lote...</Text>}

      {!loading && preview && (
        <>
          {/* Tarjetas: TMH, Humedad, TMS */}
          <Grid templateColumns="1fr 1fr 1fr" gap={4}>
            <Box bg="white" boxShadow="sm" borderRadius="lg" p={4} border="1px solid" borderColor="#e8dfd1">
              <Text fontSize="10px" fontWeight="700" color={colors.textMuted} letterSpacing="0.06em">
                PESO NETO HÚMEDO (TMH)
              </Text>
              <Text fontSize="2xl" fontWeight="800" color={colors.text}>
                {formatNumber(preview.netWeightWet, 3)}
              </Text>
            </Box>
            <Box bg="white" boxShadow="sm" borderRadius="lg" p={4} border="1px solid" borderColor="#e8dfd1">
              <Text fontSize="10px" fontWeight="700" color={colors.textMuted} letterSpacing="0.06em">
                HUMEDAD (%)
              </Text>
              <Text fontSize="2xl" fontWeight="800" color={colors.text}>
                {formatNumber(preview.moisture, 2)}
              </Text>
            </Box>
            <Box bg={brand.primary} borderRadius="lg" p={4} color="white">
              <Text fontSize="10px" fontWeight="700" letterSpacing="0.06em" opacity={0.85}>
                PESO NETO SECO (TMS)
              </Text>
              <Text fontSize="2xl" fontWeight="800">
                {formatNumber(preview.netWeightDry, 3)}
              </Text>
            </Box>
          </Grid>

          {/* Tabla de conceptos */}
          <Box bg="white" boxShadow="sm" borderRadius="lg" border="1px solid" borderColor="#e8dfd1">
            <Flex justifyContent="space-between" alignItems="center" p={4} borderBottom="1px solid" borderColor="#e8dfd1">
              <Text fontSize="sm" fontWeight="700" color={colors.text}>
                Conceptos
              </Text>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => setExtraModalOpen(true)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '6px 12px',
                    background: 'white',
                    color: brand.primary,
                    border: `1px solid ${brand.primary}`,
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  <LuPlus size={14} />
                  Añadir Otros Conceptos
                </button>
              )}
            </Flex>

            <Box>
              <Box
                bg="#faf6f0"
                px={4}
                py={2}
                display="grid"
                style={{ gridTemplateColumns: '2fr 1fr 1.5fr 1fr 40px', gap: 8 }}
              >
                <Text fontSize="10px" fontWeight="700" color={colors.textMuted}>
                  CONCEPTO / MINERAL
                </Text>
                <Text fontSize="10px" fontWeight="700" color={colors.textMuted} textAlign="right">
                  PRECIO (US$)
                </Text>
                <Text fontSize="10px" fontWeight="700" color={colors.textMuted}>
                  BASE CÁLCULO
                </Text>
                <Text fontSize="10px" fontWeight="700" color={colors.textMuted} textAlign="right">
                  IMPORTE ($)
                </Text>
                <Box />
              </Box>

              {/* Minerales */}
              {mineralLines.map((l, idx) => (
                <Box
                  key={idx}
                  px={4}
                  py={3}
                  display="grid"
                  style={{ gridTemplateColumns: '2fr 1fr 1.5fr 1fr 40px', gap: 8 }}
                  borderBottom="1px solid"
                  borderColor="#efe8dd"
                  alignItems="center"
                >
                  <Box>
                    <Text fontSize="sm" fontWeight="700" color={colors.text}>
                      {l.mineralName} ({l.mineralCode})
                    </Text>
                    <Text fontSize="xs" color={colors.textMuted}>
                      Ley: {formatNumber(l.grade, 3)} {l.unit}
                    </Text>
                  </Box>
                  <Box>
                    {readOnly ? (
                      <Text fontSize="sm" textAlign="right">
                        {formatUsd(l.unitPrice)}
                      </Text>
                    ) : (
                      <input
                        type="number"
                        step="0.01"
                        value={prices[l.mineralTypeId ?? ''] ?? l.unitPrice.toString()}
                        onChange={(e) => handlePriceChange(l.mineralTypeId ?? '', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          borderRadius: 6,
                          border: '1px solid #e8dfd1',
                          fontSize: 14,
                          fontWeight: 700,
                          textAlign: 'right',
                        }}
                      />
                    )}
                  </Box>
                  <Text fontSize="xs" color={colors.textMuted}>
                    ({formatNumber(l.grade, 3)} × {formatNumber(l.unitPrice, 2)}) × {formatNumber(l.baseCalc, 3)} TMS
                  </Text>
                  <Text
                    fontSize="sm"
                    fontWeight="700"
                    textAlign="right"
                    color={l.amount < 0 ? '#c8442f' : colors.text}
                  >
                    {formatUsd(l.amount)}
                  </Text>
                  <Box />
                </Box>
              ))}

              {/* Maquila */}
              {maquilaLine && (
                <Box
                  px={4}
                  py={3}
                  display="grid"
                  style={{ gridTemplateColumns: '2fr 1fr 1.5fr 1fr 40px', gap: 8 }}
                  borderBottom="1px solid"
                  borderColor="#efe8dd"
                  alignItems="center"
                >
                  <Box>
                    <Text fontSize="sm" fontWeight="700" color={colors.text}>
                      Costos de Tratamiento (Maquila)
                    </Text>
                  </Box>
                  <Text fontSize="xs" color={colors.textMuted} textAlign="right">
                    Fijo
                  </Text>
                  <Text fontSize="xs" color={colors.textMuted}>
                    {formatNumber(maquilaLine.baseCalc, 3)} TMS × {formatUsd(maquilaLine.unitPrice)}
                  </Text>
                  <Text fontSize="sm" fontWeight="700" textAlign="right" color="#c8442f">
                    {formatUsd(maquilaLine.amount)}
                  </Text>
                  <Box />
                </Box>
              )}

              {/* Otros conceptos */}
              {otherLines.map((l, idx) => (
                <Box
                  key={`o-${idx}`}
                  px={4}
                  py={3}
                  display="grid"
                  style={{ gridTemplateColumns: '2fr 1fr 1.5fr 1fr 40px', gap: 8 }}
                  borderBottom="1px solid"
                  borderColor="#efe8dd"
                  alignItems="center"
                >
                  <Text fontSize="sm" color={colors.text}>
                    {l.description}
                  </Text>
                  <Box />
                  <Text fontSize="xs" color={colors.textMuted}>
                    {l.kind === 'OtherCredit' ? 'Abono' : 'Descuento'}
                  </Text>
                  <Text
                    fontSize="sm"
                    fontWeight="700"
                    textAlign="right"
                    color={l.amount < 0 ? '#c8442f' : '#2a8a87'}
                  >
                    {formatUsd(l.amount)}
                  </Text>
                  {!readOnly ? (
                    <Box
                      as="button"
                      cursor="pointer"
                      color="#c8442f"
                      onClick={() => removeExtra(idx)}
                      _hover={{ color: '#9a3020' }}
                    >
                      <LuX size={14} />
                    </Box>
                  ) : (
                    <Box />
                  )}
                </Box>
              ))}
            </Box>

            {/* Total */}
            <Box bg={brand.primary} px={4} py={4} color="white" borderBottomRadius="lg">
              <Flex justifyContent="space-between" alignItems="center">
                <Text fontSize="sm" fontWeight="700" letterSpacing="0.04em">
                  TOTAL NETO A PAGAR
                </Text>
                <Text fontSize="2xl" fontWeight="800">
                  {formatUsd(preview.totalToPay)}
                </Text>
              </Flex>
            </Box>
          </Box>

          {/* Acciones */}
          {!readOnly && (
            <Flex justifyContent="flex-end" gap={2}>
              <button
                type="button"
                onClick={() => navigate('/app/cierre/liquidaciones')}
                disabled={submitting}
                style={{
                  padding: '10px 20px',
                  background: 'white',
                  color: '#4f4842',
                  border: '1px solid #e8dfd1',
                  borderRadius: 6,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleLiquidar}
                disabled={submitting || preview.totalToPay <= 0}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '10px 20px',
                  background: brand.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting || preview.totalToPay <= 0 ? 0.5 : 1,
                }}
              >
                <LuCircleCheck size={16} />
                {submitting ? 'Liquidando...' : 'Liquidar'}
              </button>
            </Flex>
          )}
        </>
      )}

      {/* Modal Agregar Concepto */}
      <Modal
        open={extraModalOpen}
        onClose={() => setExtraModalOpen(false)}
        title="Agregar Concepto"
      >
        <VStack gap={3} alignItems="stretch">
          <Box>
            <Text fontSize="xs" fontWeight="700" mb={2}>
              Tipo de Operación
            </Text>
            <Flex gap={2}>
              <Box
                as="button"
                flex="1"
                py={2}
                borderRadius="md"
                fontSize="sm"
                fontWeight="700"
                cursor="pointer"
                border="2px solid"
                borderColor={draftExtra.kind === 'OtherCredit' ? '#2a8a87' : '#e8dfd1'}
                bg={draftExtra.kind === 'OtherCredit' ? '#eef7f6' : 'white'}
                color={draftExtra.kind === 'OtherCredit' ? '#1d6664' : '#857b71'}
                onClick={() => setDraftExtra({ ...draftExtra, kind: 'OtherCredit' })}
              >
                Abono (+)
              </Box>
              <Box
                as="button"
                flex="1"
                py={2}
                borderRadius="md"
                fontSize="sm"
                fontWeight="700"
                cursor="pointer"
                border="2px solid"
                borderColor={draftExtra.kind === 'OtherDebit' ? '#c8442f' : '#e8dfd1'}
                bg={draftExtra.kind === 'OtherDebit' ? '#fae1db' : 'white'}
                color={draftExtra.kind === 'OtherDebit' ? '#9a3020' : '#857b71'}
                onClick={() => setDraftExtra({ ...draftExtra, kind: 'OtherDebit' })}
              >
                Descuento (-)
              </Box>
            </Flex>
          </Box>

          <Box>
            <Text fontSize="xs" fontWeight="700" mb={1}>
              Descripción
            </Text>
            <input
              value={draftExtra.description}
              onChange={(e) => setDraftExtra({ ...draftExtra, description: e.target.value })}
              placeholder="Ej. Adelanto efectivo, Flete..."
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid #e8dfd1',
                fontSize: 14,
              }}
            />
          </Box>

          <Box>
            <Text fontSize="xs" fontWeight="700" mb={1}>
              Importe (US$)
            </Text>
            <input
              type="number"
              step="0.01"
              value={draftExtra.amount}
              onChange={(e) => setDraftExtra({ ...draftExtra, amount: e.target.value })}
              placeholder="$ 0.00"
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid #e8dfd1',
                fontSize: 14,
                fontWeight: 700,
              }}
            />
          </Box>

          <Flex justifyContent="flex-end" gap={2}>
            <button
              type="button"
              onClick={() => setExtraModalOpen(false)}
              style={{
                padding: '8px 16px',
                background: 'white',
                color: '#4f4842',
                border: '1px solid #e8dfd1',
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={addExtra}
              style={{
                padding: '8px 16px',
                background: brand.primary,
                color: 'white',
                border: 'none',
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Guardar Concepto
            </button>
          </Flex>
        </VStack>
      </Modal>
    </VStack>
  )
}
